import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  OnDestroy,
  HostBinding,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDrawerMode, MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';

import { RouterModule } from '@angular/router';
import { ResponsiveBreakpointsService } from '@core/responsive-breakpoints.service';
import { SideMenusService } from '@shared/components/side-menus/side-menus.service';

import { TopNavbarComponent } from '@shared/components/top-navbar/top-navbar.component';
import { MatMenuModule } from '@angular/material/menu';

import { DynamicMenuComponent } from '@shared/components/side-menus/dynamic-menu/dynamic-menu.component';
import { MainMenuComponent } from '@shared/components/side-menus/main-menu/main-menu.component';

/** The basket's side sheet: beside the page on a wide screen (design 3a), over it otherwise. */
const altMenuModeFor = (screenSize: string): MatDrawerMode => (screenSize === 'large' ? 'side' : 'over');

@Component({
  selector: 'app-abook-layout-component',
  templateUrl: 'abook-layout.component.html',
  styleUrls: ['abook-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatSidenavModule, MatMenuModule, TopNavbarComponent, RouterModule, MainMenuComponent, DynamicMenuComponent],
})
export class AbookLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private sideMenusService = inject(SideMenusService);
  private responsiveBreakpointsService = inject(ResponsiveBreakpointsService);
  protected readonly altMenuMode = this.sideMenusService.altMenuMode;

  @HostBinding('class.layout-abook') layoutFlag = true;
  @HostBinding('class.main-sidenav-opened') mainSidenavOpened = false;
  @HostBinding('class.alt-sidenav-opened') altSidenavOpened = false;

  @ViewChild('mainSidenav') mainSidenav: MatSidenav;
  @ViewChild('altSidenav') altSidenav: MatSidenav;

  // Subscription to the Main Sidenav movement
  mainSidenavSubscription: Subscription;
  // Subscription to the Alt Sidenav movement
  altSidenavSubscription: Subscription;
  // Subscription to the screen size changes
  screenSizeChangeSubscription: Subscription;
  // Subscription to the main side menu mode changes
  changeMainMenuModeSubscription: Subscription;

  mainSideMenuMode: MatDrawerMode;

  constructor() {
    this.sideMenusService.altMenuMode.set(altMenuModeFor(this.responsiveBreakpointsService.currentScreenSize));
    // main menu initialization
    switch (this.responsiveBreakpointsService.currentScreenSize) {
      case 'small':
        this.mainSidenavOpened = false;
        this.mainSideMenuMode = 'push';
        break;
      case 'medium':
        this.mainSidenavOpened = true;
        this.mainSideMenuMode = 'side';
        break;
      case 'large':
        this.mainSidenavOpened = true;
        this.mainSideMenuMode = 'side';
        break;
      default:
        this.mainSidenavOpened = true;
        this.mainSideMenuMode = 'side';
    }
  }

  ngOnInit() {
    // subscribe to screen size changes
    this.screenSizeChangeSubscription = this.responsiveBreakpointsService.screenSizeChangeSubject
      .pipe(delay(0))
      .subscribe((event) => {
        this.sideMenusService.altMenuMode.set(altMenuModeFor(event));
        if (event === 'small') {
          this.sideMenusService.changeMainMenuModeSubject.next('push');
          this.sideMenusService.toggleMainMenuSubject.next('close');
        }
        if (event === 'medium') {
          this.sideMenusService.changeMainMenuModeSubject.next('side');
          this.sideMenusService.toggleMainMenuSubject.next('open');
        }
        if (event === 'large') {
          this.sideMenusService.changeMainMenuModeSubject.next('side');
          this.sideMenusService.toggleMainMenuSubject.next('open');
        }
      });
  }

  ngOnDestroy() {
    this.mainSidenavSubscription.unsubscribe();
    this.altSidenavSubscription.unsubscribe();
    this.screenSizeChangeSubscription.unsubscribe();
    this.changeMainMenuModeSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.mainSidenavSubscription = this.sideMenusService.toggleMainMenuSubject.subscribe(
      (event) => {
        switch (event) {
          case 'toggle':
            this.mainSidenav.toggle();
            break;
          case 'open':
            this.mainSidenav.open();
            break;
          case 'close':
            this.mainSidenav.close();
            break;
          default:
            this.mainSidenav.toggle();
        }
      },
      (error) => {
        console.log('toggleMainMenu [Layout B] - error', error);
      },
    );

    this.altSidenavSubscription = this.sideMenusService.toggleAltMenuSubject.subscribe(
      (event) => {
        switch (event) {
          case 'toggle':
            this.altSidenav.toggle();
            break;
          case 'open':
            this.altSidenav.open();
            break;
          case 'close':
            this.altSidenav.close();
            break;
          default:
            this.altSidenav.toggle();
        }
      },
      (error) => {
        console.log('toggleAltMenu [Layout B] - error', error);
      },
    );

    this.changeMainMenuModeSubscription = this.sideMenusService.changeMainMenuModeSubject.subscribe(
      (menuMode) => {
        this.mainSideMenuMode = menuMode;
      },
      (error) => {
        console.log('changeMainMenuModeSubject [Layout B] - error', error);
      },
    );
  }
  showFiller = false;
  // It's better to have the class applied when the opening starts as we want it ready before the opening animation ends
  openedStart(): void {
    this.mainSidenavOpened = true;
  }

  mainSidenavToggled(opened: boolean): void {
    this.mainSidenavOpened = opened;
  }

  altSidenavToggled(opened: boolean): void {
    this.altSidenavOpened = opened;
    this.sideMenusService.altMenuOpened.set(opened);
  }
}
